import { FBDocTypeEssentials, NoSqlDoc } from '@rabbit/firebase/doctype';

const DELIM = '.';
const VERSION = 3;

interface AlgoliaCreds {
  appId: string;
  searchApiKey: string;
  adminEnvVar: string;
}

const ALGOLIA_DEFAULT_LOCATION = 'eu';
const ALGOLIA_CREDS_BY_LOCATION_AND_TYPE: Record<string, AlgoliaCreds> = {
  'eu.vendable': {
    appId: 'TUDL1EJCQM',
    searchApiKey: '7f11b468b51a0184255eb362998f8a27',
    adminEnvVar: 'ALGOLIA_RABBIT_EU_VENDABLE',
  },
  'eu.retailer': {
    appId: 'LZR5UNW6G3',
    searchApiKey: 'b17c496ce7423348b3e7dbb83300bc59',
    adminEnvVar: 'ALGOLIA_RABBIT_EU_RETAILER',
  },
  'eu.category': {
    appId: 'FJILK4J0UN',
    searchApiKey: 'c02a8197ddf8654e44af65db616d34f9',
    adminEnvVar: 'ALGOLIA_RABBIT_EU_CATEGORY',
  },
  // Fix: Add key that matches what DocTypeToIndexBaseName generates
  'eu.holding': {
    appId: '95C66BAOL1',
    searchApiKey: '2b755da92dea6da9860b92ff1e79d75f',
    adminEnvVar: 'ALGOLIA_RABBIT_EU_HOLDING',
  },
  // Keep original key for backward compatibility if needed
  'eu.holding_proxy': {
    appId: '95C66BAOL1',
    searchApiKey: '2b755da92dea6da9860b92ff1e79d75f',
    adminEnvVar: 'ALGOLIA_RABBIT_EU_HOLDING_PROXY',
  },
};

/**
 * Calculates the current firebase mode, server or client
 * Sees if the client / Vite mode is set
 * Then looks for a server side environment variable
 * Finally just assumes server/emulator mode
 */
function getFirebaseMode(): string {
  // For Node.js environment
  if (typeof process === 'object' && process.env) {
    // Try FIREBASE_MODE first (for Node.js/server-side)
    if (process.env['FIREBASE_MODE']) {
      return process.env['FIREBASE_MODE'].toLowerCase();
    }
    // Then try VITE_FIREBASE_MODE (for compatibility)
    if (process.env['VITE_FIREBASE_MODE']) {
      return process.env['VITE_FIREBASE_MODE'].toLowerCase();
    }
  }

  // For browser/Vite environment
  try {
    // @ts-ignore - Vite-specific environment variables
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FIREBASE_MODE) {
      // @ts-ignore
      return import.meta.env.VITE_FIREBASE_MODE.toLowerCase();
    }
  } catch {
    // Ignore any errors trying to access import.meta
  }

  // Default to emulator if nothing else is found
  return 'emulator';
}

export function DocTypeToIndexBaseName<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>
) {
  return type.collectionName.toLowerCase().split('_')[0];
}

export function GetAlgoliaCreds<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>
) {
  const locationCode = ALGOLIA_DEFAULT_LOCATION; // just EU for now
  const projectType = DocTypeToIndexBaseName(type);
  const locType = [locationCode, projectType].join(DELIM);

  if (!ALGOLIA_CREDS_BY_LOCATION_AND_TYPE[locType]) {
    throw new Error(
      `GetAlgoliaCreds: no ALGOLIA_LOCATIONS for locationCode=${locationCode}, projectType=${projectType}`
    );
  }

  return ALGOLIA_CREDS_BY_LOCATION_AND_TYPE[locType];
}

export function GetSearchIndexName<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>
): string {
  const indexName = [
    DocTypeToIndexBaseName(type),
    getFirebaseMode(),
    'v' + VERSION,
  ].join(DELIM);

  return indexName;
}
